import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import useApi from "../../../hooks/useApi";
import useSnack from "../../../hooks/useSnack";
import SnackAlert from "../../../components/SnackAlert";

// Los 9 correos de Backend/src/utils/email.js + el boletín, en el mismo
// orden en que existen ahí. El valor es el `tipo` que espera
// POST admin/email_templates/preview (Backend/src/routes/admin/emailTemplates.js).
const TIPOS_CORREO = [
  { value: "verificacion", label: "Verificación de correo" },
  { value: "reset_password", label: "Restablecer contraseña" },
  { value: "aviso_cancion", label: "Aviso: nueva canción en el buzón" },
  { value: "retiro_confirmado", label: "Retiro confirmado" },
  { value: "medio_verificado", label: "Medio verificado" },
  { value: "feedback_verificacion", label: "Feedback de verificación" },
  { value: "nuevo_usuario_admin", label: "Aviso interno: nuevo usuario" },
  {
    value: "difusor_por_verificar_admin",
    label: "Aviso interno: difusor por verificar",
  },
  { value: "boletin", label: "Boletín" },
];

const ETIQUETA_BLOQUE = { logo: "Logo", banner: "Banner", texto: "Texto" };

const VALORES_DEFECTO = {
  nombre: "",
  colorFondo: "#f7f4ef",
  colorTarjeta: "#ffffff",
  colorAcento: "#F27224",
  colorTexto: "#1a1a1a",
  bloquesEncabezado: [
    { tipo: "logo", visible: true },
    { tipo: "banner", visible: false },
  ],
  bloquesPie: [
    { tipo: "texto", visible: true, contenido: "© RadarFreim" },
    { tipo: "logo", visible: false },
  ],
};

const CAMPOS_COLOR = [
  ["colorFondo", "Fondo de la página"],
  ["colorTarjeta", "Fondo de la tarjeta"],
  ["colorAcento", "Acento (botones, enlaces)"],
  ["colorTexto", "Texto"],
];

// Editor de bloques para el "marco" de los correos (banner, logo, colores,
// pie) — no del contenido de cada correo, que sigue siendo código
// (Backend/src/utils/email.js). "Posición" se resuelve con flechas
// arriba/abajo en vez de arrastre libre: no hay librería de drag-and-drop en
// el proyecto y los clientes de correo no soportan position/flex/grid de
// forma confiable, así que el layout final siempre se arma con tablas.
function EmailTemplateEditor({ open, templateId, onClose, onGuardado }) {
  const { loadApi, loadingApi } = useApi();
  const { showSnack, msgSB, openSB, typeSB } = useSnack();

  const [form, setForm] = useState(VALORES_DEFECTO);
  const [bannerActual, setBannerActual] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);

  const [tipoPreview, setTipoPreview] = useState("verificacion");
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewSubject, setPreviewSubject] = useState("");

  // Carga la plantilla al abrir en modo edición; en modo "nueva" arranca en
  // blanco. Se reinicia también el banner elegido localmente, para no
  // arrastrar el archivo de una edición anterior si el diálogo se reabre.
  useEffect(() => {
    if (!open) return;
    setBannerFile(null);
    setBannerPreview(null);

    if (!templateId) {
      setForm(VALORES_DEFECTO);
      setBannerActual(null);
      return;
    }

    loadApi(`admin/email_templates/${templateId}`, true, "get")
      .then((res) => {
        const d = res.data;
        setForm({
          nombre: d.nombre || "",
          colorFondo: d.colorFondo,
          colorTarjeta: d.colorTarjeta,
          colorAcento: d.colorAcento,
          colorTexto: d.colorTexto,
          bloquesEncabezado:
            d.bloquesEncabezado || VALORES_DEFECTO.bloquesEncabezado,
          bloquesPie: d.bloquesPie || VALORES_DEFECTO.bloquesPie,
        });
        setBannerActual(d.banner || null);
      })
      .catch((e) => showSnack(e.message, "error"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, templateId]);

  // Vista previa en vivo: se pide de nuevo (con un pequeño retraso, para no
  // disparar una petición por cada tecla) cada vez que cambia algo editable,
  // el tipo de correo seleccionado, o `templateId` (que cambia justo
  // después de guardar por primera vez — ver handleGuardar). El banner
  // recién elegido no se refleja aquí hasta guardar: la vista previa usa el
  // que ya esté persistido para esta plantilla (ninguno, en una nueva),
  // porque el archivo en sí no viaja hasta el guardado (multipart).
  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => {
      loadApi("admin/email_templates/preview", true, "post", {
        tipo: tipoPreview,
        templateId: templateId || undefined,
        config: {
          colorFondo: form.colorFondo,
          colorTarjeta: form.colorTarjeta,
          colorAcento: form.colorAcento,
          colorTexto: form.colorTexto,
          bloquesEncabezado: form.bloquesEncabezado,
          bloquesPie: form.bloquesPie,
        },
      })
        .then((res) => {
          setPreviewHtml(res.data.html);
          setPreviewSubject(res.data.subject);
        })
        .catch(() => {});
    }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tipoPreview, templateId, form]);

  const moverBloque = (grupo, index, delta) => {
    setForm((f) => {
      const lista = [...f[grupo]];
      const destino = index + delta;
      if (destino < 0 || destino >= lista.length) return f;
      [lista[index], lista[destino]] = [lista[destino], lista[index]];
      return { ...f, [grupo]: lista };
    });
  };

  const toggleBloque = (grupo, index) => {
    setForm((f) => ({
      ...f,
      [grupo]: f[grupo].map((b, i) =>
        i === index ? { ...b, visible: !b.visible } : b
      ),
    }));
  };

  const editarTextoBloque = (grupo, index, contenido) => {
    setForm((f) => ({
      ...f,
      [grupo]: f[grupo].map((b, i) => (i === index ? { ...b, contenido } : b)),
    }));
  };

  const handleBanner = (e) => {
    const archivo = e.target.files[0];
    if (!archivo) return;
    setBannerFile(archivo);
    setBannerPreview(URL.createObjectURL(archivo));
  };

  const handleGuardar = () => {
    if (!form.nombre.trim()) {
      showSnack("Ponle un nombre a la plantilla", "warning");
      return;
    }

    const body = {
      nombre: form.nombre.trim(),
      colorFondo: form.colorFondo,
      colorTarjeta: form.colorTarjeta,
      colorAcento: form.colorAcento,
      colorTexto: form.colorTexto,
      // Se manda como texto, no como arreglo: loadApi (Frontend/src/hooks/useApi.js)
      // arma el multipart recorriendo con forEach los valores que son
      // objetos, y eso rompería un arreglo de bloques (cada elemento
      // terminaría como un campo suelto). El backend lo parsea con
      // JSON.parse.
      bloquesEncabezado: JSON.stringify(form.bloquesEncabezado),
      bloquesPie: JSON.stringify(form.bloquesPie),
    };
    if (bannerFile) body.banner = bannerFile;

    const endpoint = templateId
      ? `admin/email_templates/${templateId}`
      : "admin/email_templates";
    const metodo = templateId ? "put" : "post";

    loadApi(endpoint, true, metodo, body, true)
      .then((res) => {
        showSnack(res.data.message);
        // El banner recién elegido no se reflejaba en la vista previa hasta
        // reabrir el editor: ya guardado, se toma directo de la respuesta
        // (conURL en el backend ya la devuelve con el prefijo ASSETS
        // puesto) y se limpia el archivo local, que ahora es redundante.
        setBannerFile(null);
        setBannerPreview(null);
        setBannerActual(res.data.plantilla?.banner || bannerActual);
        // Se avisa al padre con el id: si era una plantilla nueva, pasa a
        // quedar "en edición" con ese id (guardados siguientes son PUT, no
        // POST) y la vista previa puede resolver el banner recién guardado
        // sin que el admin tenga que cerrar y reabrir el diálogo.
        onGuardado(res.data.plantilla?.id);
      })
      .catch((e) => showSnack(e.message, "error"));
  };

  const guardando = loadingApi.includes(
    `${templateId ? "put" : "post"}__admin/email_templates${
      templateId ? `/${templateId}` : ""
    }`
  );

  const renderBloques = (grupo, titulo) => (
    <Stack gap={1}>
      <Typography variant="subtitle2">{titulo}</Typography>
      {form[grupo].map((bloque, i) => (
        <Stack
          key={`${bloque.tipo}-${i}`}
          direction="row"
          alignItems="center"
          gap={1}
          sx={{ border: "1px solid #e0e0e0", borderRadius: 1.5, p: 1 }}
        >
          <Switch
            size="small"
            checked={!!bloque.visible}
            onChange={() => toggleBloque(grupo, i)}
          />
          <Typography variant="body2" sx={{ minWidth: 60 }}>
            {ETIQUETA_BLOQUE[bloque.tipo]}
          </Typography>
          {bloque.tipo === "texto" ? (
            <TextField
              size="small"
              value={bloque.contenido || ""}
              onChange={(e) => editarTextoBloque(grupo, i, e.target.value)}
              placeholder="© RadarFreim"
              sx={{ flexGrow: 1 }}
            />
          ) : (
            <Box sx={{ flexGrow: 1 }} />
          )}
          <IconButton
            size="small"
            disabled={i === 0}
            onClick={() => moverBloque(grupo, i, -1)}
          >
            <ArrowUpwardIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            disabled={i === form[grupo].length - 1}
            onClick={() => moverBloque(grupo, i, 1)}
          >
            <ArrowDownwardIcon fontSize="small" />
          </IconButton>
        </Stack>
      ))}
    </Stack>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {templateId ? "Editar plantilla" : "Nueva plantilla"}
      </DialogTitle>
      <DialogContent dividers>
        <Stack direction={{ xs: "column", md: "row" }} gap={3}>
          {/* Controles */}
          <Stack gap={2.5} sx={{ flex: 1, minWidth: 0 }}>
            <TextField
              label="Nombre de la plantilla"
              value={form.nombre}
              onChange={(e) =>
                setForm((f) => ({ ...f, nombre: e.target.value }))
              }
              fullWidth
            />

            <Stack gap={1}>
              <Typography variant="subtitle2">Banner</Typography>
              <Box
                sx={{
                  border: "1px dashed #ccc",
                  borderRadius: 1.5,
                  p: 1,
                  bgcolor: "#fafafa",
                  minHeight: 90,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {bannerPreview || bannerActual ? (
                  <img
                    src={bannerPreview || bannerActual}
                    alt="Banner"
                    style={{
                      maxWidth: "100%",
                      maxHeight: 120,
                      objectFit: "contain",
                    }}
                  />
                ) : (
                  <Typography variant="caption" color="grayText">
                    Sin banner
                  </Typography>
                )}
              </Box>
              <Button
                component="label"
                startIcon={<AttachFileIcon />}
                size="small"
                sx={{ alignSelf: "flex-start" }}
              >
                {bannerActual || bannerPreview ? "Cambiar banner" : "Subir banner"}
                <input
                  type="file"
                  hidden
                  accept=".jpg,.jpeg,.png"
                  onChange={handleBanner}
                />
              </Button>
            </Stack>

            {renderBloques("bloquesEncabezado", "Encabezado")}
            {renderBloques("bloquesPie", "Pie de página")}

            <Stack gap={1}>
              <Typography variant="subtitle2">Colores</Typography>
              <Stack direction="row" flexWrap="wrap" gap={2}>
                {CAMPOS_COLOR.map(([campo, etiqueta]) => (
                  <Stack key={campo} direction="row" alignItems="center" gap={1}>
                    <input
                      type="color"
                      value={form[campo]}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, [campo]: e.target.value }))
                      }
                      style={{
                        width: 36,
                        height: 36,
                        border: "none",
                        padding: 0,
                        background: "none",
                      }}
                    />
                    <Typography variant="caption">{etiqueta}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Stack>
          </Stack>

          {/* Vista previa en vivo */}
          <Stack gap={1} sx={{ flex: 1, minWidth: 0 }}>
            <TextField
              select
              size="small"
              label="Vista previa de"
              value={tipoPreview}
              onChange={(e) => setTipoPreview(e.target.value)}
            >
              {TIPOS_CORREO.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </TextField>
            <Typography variant="caption" color="grayText">
              Asunto: {previewSubject || "—"}
            </Typography>
            <Box
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: 1.5,
                overflow: "hidden",
                height: 520,
              }}
            >
              {/* iframe (no dangerouslySetInnerHTML) para aislar por completo
                  los estilos del correo —incluido el fondo de página y el de
                  la tarjeta, que aquí son parte de lo que se previsualiza—
                  de los estilos del panel de administración. */}
              <iframe
                title="Vista previa del correo"
                srcDoc={previewHtml}
                style={{ width: "100%", height: "100%", border: "none" }}
              />
            </Box>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
        <LoadingButton variant="contained" loading={guardando} onClick={handleGuardar}>
          Guardar
        </LoadingButton>
      </DialogActions>
      <SnackAlert openSB={openSB} typeSB={typeSB} msgSB={msgSB} />
    </Dialog>
  );
}

export default EmailTemplateEditor;
